import { useState, useEffect } from 'react';
import { ChevronLeft, Clock, Sun, Moon, Coffee, Zap, Calendar, ToggleLeft, ToggleRight, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TimeSlot {
    start: string;
    end: string;
}

interface SchedulePreferences {
    workingHours: TimeSlot;
    focusTime: {
        enabled: boolean;
        preferredTime: 'morning' | 'afternoon' | 'evening';
        duration: number; // minutes
    };
    breaks: {
        enabled: boolean;
        frequency: number; // minutes
        duration: number; // minutes
    };
    bufferTime: number; // minutes between meetings
    workDays: boolean[]; // Sun-Sat
    avoidEarlyMeetings: boolean;
    avoidLateMeetings: boolean;
    lunchProtection: {
        enabled: boolean;
        start: string;
        end: string;
    };
}

const STORAGE_KEY = 'way2will_schedule_preferences';

const defaultPreferences: SchedulePreferences = {
    workingHours: { start: '09:00', end: '17:00' },
    focusTime: { enabled: true, preferredTime: 'morning', duration: 120 },
    breaks: { enabled: true, frequency: 90, duration: 15 },
    bufferTime: 15,
    workDays: [false, true, true, true, true, true, false], // Mon-Fri
    avoidEarlyMeetings: true,
    avoidLateMeetings: true,
    lunchProtection: { enabled: true, start: '12:00', end: '13:00' },
};

export default function AppAutoSchedulePreferences() {
    const navigate = useNavigate();
    const [prefs, setPrefs] = useState<SchedulePreferences>(defaultPreferences);
    const [saved, setSaved] = useState(false);

    // Load from storage
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                setPrefs({ ...defaultPreferences, ...JSON.parse(stored) });
            } catch (e) {
                console.error('Error loading schedule preferences:', e);
            }
        }
    }, []);

    const handleSave = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const updatePrefs = (updates: Partial<SchedulePreferences>) => {
        setPrefs(prev => ({ ...prev, ...updates }));
    };

    const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                    >
                        <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                    </button>
                    <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Auto-Schedule Preferences</h1>
                </div>
                <button
                    onClick={handleSave}
                    className={`px-4 py-2 rounded-xl font-semibold text-sm flex items-center gap-2 transition ${saved
                            ? 'bg-[#D5F2EA] text-[#3DA58A]'
                            : 'bg-[#0D5F6B] text-white hover:bg-[#0A4E59]'
                        }`}
                >
                    <Save className="w-4 h-4" />
                    {saved ? 'Saved!' : 'Save'}
                </button>
            </div>

            {/* Working Hours */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Clock className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Working Hours</h2>
                        <p className="text-xs text-[#7B8C9B]">When the AI can schedule tasks and events</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs text-[#7B8C9B] mb-1">Start Time</label>
                        <input
                            type="time"
                            value={prefs.workingHours.start}
                            onChange={(e) => updatePrefs({
                                workingHours: { ...prefs.workingHours, start: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-[#7B8C9B] mb-1">End Time</label>
                        <input
                            type="time"
                            value={prefs.workingHours.end}
                            onChange={(e) => updatePrefs({
                                workingHours: { ...prefs.workingHours, end: e.target.value }
                            })}
                            className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white"
                        />
                    </div>
                </div>
            </section>

            {/* Work Days */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Work Days</h2>
                        <p className="text-xs text-[#7B8C9B]">Days when tasks can be scheduled</p>
                    </div>
                </div>
                <div className="flex justify-between">
                    {dayLabels.map((day, index) => (
                        <button
                            key={index}
                            onClick={() => {
                                const newDays = [...prefs.workDays];
                                newDays[index] = !newDays[index];
                                updatePrefs({ workDays: newDays });
                            }}
                            className={`w-10 h-10 rounded-full font-semibold text-sm transition ${prefs.workDays[index]
                                    ? 'bg-[#0D5F6B] text-white'
                                    : 'bg-[#F5F8FA] dark:bg-dark-elevated text-[#7B8C9B]'
                                }`}
                        >
                            {day}
                        </button>
                    ))}
                </div>
            </section>

            {/* Focus Time */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                            <Zap className="w-5 h-5 text-[#0D5F6B]" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Focus Time</h2>
                            <p className="text-xs text-[#7B8C9B]">Protect time for deep work</p>
                        </div>
                    </div>
                    <button
                        onClick={() => updatePrefs({
                            focusTime: { ...prefs.focusTime, enabled: !prefs.focusTime.enabled }
                        })}
                    >
                        {prefs.focusTime.enabled ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>
                {prefs.focusTime.enabled && (
                    <div className="space-y-3 pl-13">
                        <div>
                            <label className="block text-xs text-[#7B8C9B] mb-2">Preferred Time</label>
                            <div className="flex gap-2">
                                {(['morning', 'afternoon', 'evening'] as const).map((time) => (
                                    <button
                                        key={time}
                                        onClick={() => updatePrefs({
                                            focusTime: { ...prefs.focusTime, preferredTime: time }
                                        })}
                                        className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 ${prefs.focusTime.preferredTime === time
                                                ? 'bg-[#0D5F6B] text-white'
                                                : 'bg-[#F5F8FA] dark:bg-dark-elevated text-[#4A5C6A]'
                                            }`}
                                    >
                                        {time === 'morning' && <Sun className="w-4 h-4" />}
                                        {time === 'afternoon' && <Coffee className="w-4 h-4" />}
                                        {time === 'evening' && <Moon className="w-4 h-4" />}
                                        {time.charAt(0).toUpperCase() + time.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs text-[#7B8C9B] mb-2">Duration: {prefs.focusTime.duration} min</label>
                            <input
                                type="range"
                                min="30"
                                max="240"
                                step="30"
                                value={prefs.focusTime.duration}
                                onChange={(e) => updatePrefs({
                                    focusTime: { ...prefs.focusTime, duration: parseInt(e.target.value) }
                                })}
                                className="w-full accent-[#0D5F6B]"
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* Lunch Protection */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                            <Coffee className="w-5 h-5 text-[#0D5F6B]" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Lunch Protection</h2>
                            <p className="text-xs text-[#7B8C9B]">Block time for meals</p>
                        </div>
                    </div>
                    <button
                        onClick={() => updatePrefs({
                            lunchProtection: { ...prefs.lunchProtection, enabled: !prefs.lunchProtection.enabled }
                        })}
                    >
                        {prefs.lunchProtection.enabled ? (
                            <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                        ) : (
                            <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                        )}
                    </button>
                </div>
                {prefs.lunchProtection.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-[#7B8C9B] mb-1">Start</label>
                            <input
                                type="time"
                                value={prefs.lunchProtection.start}
                                onChange={(e) => updatePrefs({
                                    lunchProtection: { ...prefs.lunchProtection, start: e.target.value }
                                })}
                                className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-[#7B8C9B] mb-1">End</label>
                            <input
                                type="time"
                                value={prefs.lunchProtection.end}
                                onChange={(e) => updatePrefs({
                                    lunchProtection: { ...prefs.lunchProtection, end: e.target.value }
                                })}
                                className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white"
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* Buffer Time */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <Clock className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">Buffer Time</h2>
                        <p className="text-xs text-[#7B8C9B]">Time between meetings: {prefs.bufferTime} min</p>
                    </div>
                </div>
                <input
                    type="range"
                    min="0"
                    max="30"
                    step="5"
                    value={prefs.bufferTime}
                    onChange={(e) => updatePrefs({ bufferTime: parseInt(e.target.value) })}
                    className="w-full accent-[#0D5F6B]"
                />
                <div className="flex justify-between text-xs text-[#7B8C9B] mt-1">
                    <span>0 min</span>
                    <span>30 min</span>
                </div>
            </section>

            {/* Meeting Preferences */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-3">Meeting Preferences</h2>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-[#1A2832] dark:text-white">Avoid early morning meetings</span>
                        <button
                            onClick={() => updatePrefs({ avoidEarlyMeetings: !prefs.avoidEarlyMeetings })}
                        >
                            {prefs.avoidEarlyMeetings ? (
                                <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                            ) : (
                                <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                            )}
                        </button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-[#1A2832] dark:text-white">Avoid late evening meetings</span>
                        <button
                            onClick={() => updatePrefs({ avoidLateMeetings: !prefs.avoidLateMeetings })}
                        >
                            {prefs.avoidLateMeetings ? (
                                <ToggleRight className="w-10 h-6 text-[#0D5F6B]" />
                            ) : (
                                <ToggleLeft className="w-10 h-6 text-[#7B8C9B]" />
                            )}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
